import { useEffect, useMemo } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './context/useAuth';

// Core
import Menus from '@/src/ui/Menus';

// Auth
import LoginPage from '@/src/pages/_auth/login/LoginPage';
import LogoutPage from '@/src/pages/_auth/logout/LogoutPage';
import ImpersonateHandler from '@/src/pages/rostering/users/components/ImpersonationHandler';
import ResetPasswordPage from '@/src/pages/_auth/resetPassword/ResetPasswordPage';

// User
import DashboardPage from '@/src/pages/user/dashboard/DashboardPage';
// import UserProfilePage from '@/src/pages/user/userProfile/UserProfilePage';

// Student Exam View
import SEV_CreateExamAttemptPage from '@/src/pages/studentExamView/createExamAttempt/CreateExamAttemptPage';
import SEV_StartExamPage from '@/src/pages/studentExamView/startExam/StartExamPage';
import SEV_ViewQuestionPage from '@/src/pages/studentExamView/viewQuestions/ViewQuestionPage';
import SEV_AttemptSummaryPage from '@/src/pages/studentExamView/viewQuestions/AttemptSummaryPage';
import SEV_ExamResultsPage from '@/src/pages/studentExamView/examResults/ExamResultsPage';

// Rostering
import UsersPage from '@/src/pages/rostering/users/UsersPage';
import DistrictsPage from '@/src/pages/rostering/districts/DistrictsPage';
import SchoolsPage from '@/src/pages/rostering/schools/SchoolsPage';
import ClassesPage from '@/src/pages/rostering/classes/ClassesPage';
import NightlySyncPage from '@/src/pages/rostering/nightlySync/NightlySyncPage';

// Questions
import CreateEditQuestionPage from '@/src/pages/questions/createEditQuestion/CreateEditQuestionPage';
import ViewQuestionsPage from '@/src/pages/questions/viewQuestions/ViewQuestionsPage';

// Exams Admin
import CreateEditExamPage from '@/src/pages/examsAdmin/createEditExam/CreateEditExamPage';
import ViewExamsPage from '@/src/pages/examsAdmin/viewExams/ViewExamsPage';
import StandardsPage from '@/src/pages/examsAdmin/standards/StandardsPage';

// Exams
import ExamAssignmentsPage from '@/src/pages/exams/examAssignments/ExamAssignmentsPage';
import DynamicQuizGenerator from '@/src/pages/exams/dynamicQuizGenerator/DynamicQuizGeneratorPage';
import YourQuizzesPage from '@/src/pages/exams/yourQuizzes/YourQuizzesPage';
import ViewExamAttemptsPage from '@/src/pages/exams/viewExamAttempts/ViewExamAttemptsPage';
import HandscoreQuestionsPage from '@/src/pages/exams/handscoreQuestions/HandscoreQuestionsPage';
import AttemptDetailsPage from '@/src/pages/exams/attemptDetails/AttemptDetailsPage';

// Reports - Main
import ClassOverviewPage from '@/src/pages/reports/classOverview/ClassOverviewPage';
import ComparisonSummaryPage from '@/src/pages/reports/comparisonSummary/ComparisonSummaryPage';
import CompletionStatusReportPage from '@/src/pages/reports/completionStatusReport/CompletionStatusReportPage';
import ItemAnalysisPage from '@/src/pages/reports/itemAnalysis/ItemAnalysisPage';
import ClassStandardsReportPage from '@/src/pages/reports/standardsReport/class/ClassStandardsReportPage';
import StudentHistoryPage from '@/src/pages/reports/studentHistory/StudentHistoryPage';
import SubmissionReportPage from '@/src/pages/reports/submissionReport/SubmissionReportPage';
import AssessmentHistoryPage from '@/src/pages/reports/assessmentHistory/AssessmentHistoryPage';

// Reports - Helpers
import StudentStandardsReportPage from '@/src/pages/reports/standardsReport/student/StudentStandardsReportPage';
import QuestionListPage from '@/src/pages/reports/questionList/QuestionListPage';

// Routing & Auth
// import NotFoundPage from '@/src/pages/_routing/NotFoundPage';
import NotAuthorizedPage from '@/src/pages/_routing/NotAuthorizedPage';
import ProtectedRoute from './components/ProtectedRoute';
import PublicOnlyRoute from './components/PublicOnlyRoute';

// User Profile Page
import UserProfilePage from '../pages/user/userProfile/UserProfilePage';

import Roles from './Roles';
import { sanitizeGradeShortCode } from '@/src/utils';

function RoutesWrapper() {
    const location = useLocation();
    const isExamView = useMemo(() => {
        const re = /^\/?studentExamView\/attempt\/(?<attemptId>[^/]+)\/(?:(?:q|summary|results))/i;
        return re.test(location.pathname);
    }, [location.pathname]);

    const { user, loading } = useAuth();
    useEffect(() => {
        const grade = user?.alternateGradeColorShortCode
            ? sanitizeGradeShortCode(user?.alternateGradeColorShortCode)
            : sanitizeGradeShortCode(user?.gradeShortCode);
        document.documentElement.style.setProperty(
            '--scrollbar-color',
            `var(--pm-g${grade}-light)`
        );
        document.documentElement.style.setProperty(
            '--user-min-font-size',
            `${Math.max(user?.minFontSize ?? 12, 12)}pt`
        );
    }, [user?.alternateGradeColorShortCode, user?.gradeShortCode, user?.minFontSize]);

    const isLoggedIn = !!user;
    const defaultNavTo = isLoggedIn ? '/user/dashboard' : '/login';

    return (
        <Routes>
            {/* <Route path="/" element={<Navigate to={defaultNavTo} replace />} /> */}
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route element={<PublicOnlyRoute userExists={!!user} loading={loading} />}>
                <Route path="/login" element={<LoginPage />} />
            </Route>
            <Route
                path="/logout"
                element={
                    <ProtectedRoute
                        allowedRoles={[
                            Roles.SuperAdmin,
                            Roles.DistrictAdmin,
                            Roles.SchoolAdmin,
                            Roles.Teacher,
                            Roles.Student,
                            Roles.Coach,
                        ]}
                    >
                        <LogoutPage />
                    </ProtectedRoute>
                }
            />

            <Route path="/*" element={<Menus isExamView={isExamView} />}>
                <Route
                    path="user/dashboard"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                                Roles.Student,
                                Roles.Coach,
                            ]}
                        >
                            <DashboardPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="user/userProfile"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                                Roles.Student,
                                Roles.Coach,
                            ]}
                        >
                            <UserProfilePage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="impersonate"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <ImpersonateHandler />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="studentExamView/attempt/new"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <SEV_CreateExamAttemptPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="studentExamView/attempt/:attemptId/start"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin, Roles.Student]}>
                            <SEV_StartExamPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="studentExamView/attempt/:attemptId/q/:attemptQuestionId"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin, Roles.Student]}>
                            <SEV_ViewQuestionPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="studentExamView/attempt/:attemptId/summary/:lastQuestionId"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin, Roles.Student]}>
                            <SEV_AttemptSummaryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="studentExamView/attempt/:attemptId/results"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin, Roles.Student]}>
                            <SEV_ExamResultsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="rostering/users"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <UsersPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="rostering/districts"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                            ]}
                        >
                            <DistrictsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="rostering/schools"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                            ]}
                        >
                            <SchoolsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="rostering/classes"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ClassesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="rostering/nightlySync"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <NightlySyncPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="questions/createEditQuestion"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <CreateEditQuestionPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="questions/viewQuestions"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <ViewQuestionsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="examsAdmin/createEditExam"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <CreateEditExamPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="examsAdmin/viewExams"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <ViewExamsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="examsAdmin/standards"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin]}>
                            <StandardsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="exams/viewExamAttempts"
                    element={
                        <ProtectedRoute allowedRoles={[Roles.SuperAdmin, Roles.Student]}>
                            <ViewExamAttemptsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="exams/examAssignments"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ExamAssignmentsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="exams/dynamicQuizGenerator"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                // Roles.DistrictAdmin,
                                // Roles.SchoolAdmin,
                                // Roles.Teacher,
                                Roles.Coach,
                            ]}
                        >
                            <DynamicQuizGenerator />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="exams/yourQuizzes"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                // Roles.DistrictAdmin,
                                // Roles.SchoolAdmin,
                                // Roles.Teacher,
                                Roles.Coach,
                            ]}
                        >
                            <YourQuizzesPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="exams/handscoreQuestions"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <HandscoreQuestionsPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="exams/attemptDetails"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <AttemptDetailsPage />
                        </ProtectedRoute>
                    }
                />

                <Route
                    path="reports/classOverview"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ClassOverviewPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/comparisonSummary"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ComparisonSummaryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/assessmentHistory"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <AssessmentHistoryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/completionStatusReport"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <CompletionStatusReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/itemAnalysis"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ItemAnalysisPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/standardsReport/class"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <ClassStandardsReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/standardsReport/student"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <StudentStandardsReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/studentHistory"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                                Roles.Student,
                            ]}
                        >
                            <StudentHistoryPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/submissionReport"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <SubmissionReportPage />
                        </ProtectedRoute>
                    }
                />
                <Route
                    path="reports/questionList/:examId"
                    element={
                        <ProtectedRoute
                            allowedRoles={[
                                Roles.SuperAdmin,
                                Roles.DistrictAdmin,
                                Roles.SchoolAdmin,
                                Roles.Teacher,
                            ]}
                        >
                            <QuestionListPage />
                        </ProtectedRoute>
                    }
                />

                <Route path="*" element={<Navigate to={defaultNavTo} replace />} />
                <Route path="not-authorized" element={<NotAuthorizedPage />} />
            </Route>
        </Routes>
    );
}

export default RoutesWrapper;
